import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useUserContext } from '../UserContext';
import classNames from 'classnames';
import { CommentD, SagaLocation, SpaceOperations } from '@saga/shared';
import { stringToColor } from '@/../../shared/src/utils/Colors';
import { usePerformActionWithYBlocks } from '../RealtimeDocumentProvider';
import { CornerDownLeft, Trash } from 'react-feather';
import { PopOver, usePopover } from '../popover/PopOver';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { CommentIconSvg } from '../icons/CommentIconSvg';
import { CommenterInitialsIcon } from '../icons/CommenterInitialsIcon';
import { useMembers } from '../MembersProvider';
import MemberAvatar from '../MemberAvatar';
import { AVAILABLE_COLORS } from '@/constants';

interface CommentIconProps {
    blockId: string;
    onCommentClick: () => void;
    comments?: CommentD[];
    isActiveAndPopoverOpen: boolean;
}

const CommentIconComponent = ({ blockId, onCommentClick, comments, isActiveAndPopoverOpen }: CommentIconProps) => {
    const [isHovered, setIsHovered] = useState(false);
    const hasComments = comments && comments?.length > 0;
    const { getMember } = useMembers();

    const processedCommenterInitials = useMemo(() => {
        const currentHasComments = comments && comments.length > 0;
        if (!currentHasComments || !comments) {
            return [];
        }
        const uniqueCommenterIds = [...new Set(comments.map((c) => c.userId).filter((id): id is string => !!id))];
        return uniqueCommenterIds.slice(0, 3).map((userId) => {
            const member = getMember(userId);
            let initial = '?';
            if (member?.firstName) {
                initial = member.firstName.charAt(0);
            } else if (member?.lastName) {
                initial = member.lastName.charAt(0);
            }
            return { initial: initial, id: userId };
        });
    }, [comments, getMember]);

    const handleIconClick = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            onCommentClick();
        },
        [onCommentClick],
    );

    return (
        <div
            className={classNames(
                'pl-1 comment-icon-container relative select-none user-select-none',
                'transition-opacity duration-150',
                {
                    'opacity-75 hover:opacity-100': hasComments,
                    'opacity-100': !hasComments && (isHovered || isActiveAndPopoverOpen),
                    'opacity-0': !hasComments && !isHovered && !isActiveAndPopoverOpen,
                },
            )}
            onMouseDown={(e) => e.preventDefault()}
            data-testid={`comment-icon-${blockId}`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div
                className={classNames('flex items-center justify-start w-8 h-6 rounded-full cursor-pointer')}
                onClick={handleIconClick}
            >
                {hasComments && processedCommenterInitials.length > 0 ? (
                    <CommenterInitialsIcon commenters={processedCommenterInitials} />
                ) : (
                    <div className="flex items-center justify-center w-6 h-6 rounded-full shadow-sm hover:shadow bg-white dark:bg-zinc-700">
                        <CommentIconSvg className={'text-gray-400 hover:text-gray-500'} />
                    </div>
                )}
            </div>
        </div>
    );
};

export const CommentIcon = React.memo(CommentIconComponent);

interface CommentsPopOverProps {
    comments?: CommentD[];
    blockId: string;
    entityId: string;
    onClose: () => void;
    setShowCommentCard: (show: boolean) => void;
    showCommentCard: boolean;
    attachToRef: React.RefObject<HTMLDivElement>;
}

export default function CommentsPopOver({
    comments,
    blockId,
    entityId,
    setShowCommentCard,
    showCommentCard,
    attachToRef,
}: CommentsPopOverProps): JSX.Element | null {
    const [newCommentText, setNewCommentText] = useState('');
    const [shouldScrollToLatest, setShouldScrollToLatest] = useState(false);

    const { user } = useUserContext();
    const { getMember } = useMembers();
    const { t } = useTranslation();
    const performActionWithBlocks = usePerformActionWithYBlocks();

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const latestCommentRef = useRef<HTMLDivElement>(null);
    const commentsContainerRef = useRef<HTMLDivElement>(null);

    const xOverflow = 400;
    const yOverflow = 100;

    const [popoverRef, contentRef] = usePopover({
        isOpen: showCommentCard,
        onClose: () => setShowCommentCard(false),
        target: useCallback(() => {
            if (attachToRef.current) {
                return attachToRef.current.getBoundingClientRect();
            }
            return null;
        }, [attachToRef]),
        clickOutsideRefs: [attachToRef],
        position: 'below',
        align: isMobile ? 'right' : 'left',
        overflow: {
            x: xOverflow,
            y: yOverflow,
        },
    });

    useEffect(() => {
        if (shouldScrollToLatest && comments && comments.length > 0 && latestCommentRef.current) {
            latestCommentRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            setShouldScrollToLatest(false);
        }
    }, [shouldScrollToLatest, comments]);

    const getCommenterName = (userId: string | undefined | null): string => {
        if (!userId) return t('comments.annonymous_user');

        const member = getMember(userId);
        if (member) {
            if (member.firstName || member.lastName) {
                return `${member.firstName || ''} ${member.lastName || ''}`.trim();
            }
            if (userId === user?.id && user?.data.email) {
                return user.data.email;
            }
            return t('comments.member_user', { id: userId.substring(0, 6) });
        }

        return t('comments.unknown_user');
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (blockId && newCommentText.trim()) {
            performActionWithBlocks(SagaLocation.pageLocationFromId(entityId), (blocks) => {
                SpaceOperations.addComment(blocks, blockId, newCommentText, user?.id);
            });
            setNewCommentText('');
            setShouldScrollToLatest(true);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();

            if (blockId && newCommentText.trim()) {
                performActionWithBlocks(SagaLocation.pageLocationFromId(entityId), (blocks) => {
                    SpaceOperations.addComment(blocks, blockId, newCommentText, user?.id);
                });
                setNewCommentText('');
                setShouldScrollToLatest(true);
            }
        }
    };

    const handleDelete = (commentId: string) => {
        performActionWithBlocks(SagaLocation.pageLocationFromId(entityId), (blocks) => {
            SpaceOperations.deleteComment(blocks, blockId, commentId);
        });
    };

    const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNewCommentText(e.target.value);
    };

    return (
        <PopOver
            isOpen={showCommentCard}
            ref={popoverRef}
            contentRef={contentRef}
            zIndex={400}
            onOpenAnimationDone={() => {
                textareaRef.current?.focus();
            }}
        >
            <div className="w-80 bg-white dark:bg-zinc-800 shadow-small rounded-md z-150" data-testid="comment-card">
                <div className="p-1 flex flex-col h-full max-h-[500px]">
                    {comments && comments.length > 0 && (
                        <div className="space-y-1.5 my-3 px-2 overflow-y-auto" ref={commentsContainerRef}>
                            {comments.map((comment, index) => {
                                const commentDate = new Date(comment.createdAt);
                                const now = new Date();
                                const isLastComment = index === comments.length - 1;
                                const commenterName = getCommenterName(comment.userId);
                                const commenterColor = comment.userId
                                    ? stringToColor(comment.userId, AVAILABLE_COLORS)
                                    : AVAILABLE_COLORS[0];

                                const isToday =
                                    commentDate.getDate() === now.getDate() &&
                                    commentDate.getMonth() === now.getMonth() &&
                                    commentDate.getFullYear() === now.getFullYear();
                                const isCurrentYear = commentDate.getFullYear() === now.getFullYear();

                                const timeFormatOptions: Intl.DateTimeFormatOptions = {
                                    hour: 'numeric',
                                    minute: 'numeric',
                                    hour12: true,
                                };
                                const dateFormatOptions: Intl.DateTimeFormatOptions = {
                                    month: 'numeric',
                                    day: 'numeric',
                                };
                                const yearFormatOptions: Intl.DateTimeFormatOptions = {
                                    year: 'numeric',
                                    month: 'numeric',
                                    day: 'numeric',
                                };

                                const dateString = isToday
                                    ? commentDate.toLocaleTimeString(undefined, timeFormatOptions)
                                    : isCurrentYear
                                      ? `${commentDate.toLocaleDateString(undefined, dateFormatOptions)}, ${commentDate.toLocaleTimeString(undefined, timeFormatOptions)}`
                                      : `${commentDate.toLocaleDateString(undefined, yearFormatOptions)}, ${commentDate.toLocaleTimeString(undefined, timeFormatOptions)}`;

                                return (
                                    <div
                                        key={comment.id}
                                        ref={isLastComment ? latestCommentRef : null}
                                        className="text-sm group"
                                    >
                                        <div className="space-y-1">
                                            <div className="flex justify-between items-center">
                                                <div className="flex items-center space-x-1.5">
                                                    <MemberAvatar
                                                        name={commenterName}
                                                        color={commenterColor}
                                                        size="md"
                                                        showBorder={false}
                                                    />
                                                    <span className="font-medium text-sm text-gray-900 dark:text-gray-100">
                                                        {commenterName}
                                                    </span>
                                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                                        {dateString}
                                                    </span>
                                                </div>
                                                <button
                                                    onClick={() => handleDelete(comment.id)}
                                                    className="text-gray-400 hover:text-red-500 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                                                    aria-label="Delete comment"
                                                >
                                                    <Trash size={12} />
                                                </button>
                                            </div>
                                            <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap pb-1">
                                                {comment.text}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="mt-auto sticky bottom-0 bg-white dark:bg-zinc-800 p-1">
                        <form onSubmit={handleSubmit} className="flex items-end space-x-1">
                            <textarea
                                ref={textareaRef}
                                className="flex-grow p-1 border-none rounded-md dark:bg-zinc-700 dark:text-white text-sm overflow-hidden resize-none focus:outline-none max-h-40"
                                placeholder={t('comments.add_comment_placeholder')}
                                value={newCommentText}
                                onChange={handleTextAreaChange}
                                onKeyDown={handleKeyDown}
                                style={{ fieldSizing: 'content' } as React.CSSProperties}
                                data-testid="comment-card-textarea"
                            />
                            <button
                                type="submit"
                                disabled={!newCommentText.trim()}
                                className={classNames(
                                    'rounded h-7 w-7 flex items-center justify-center flex-shrink-0',
                                    {
                                        'cursor-pointer hover:bg-gray-100 dark:hover:bg-zinc-600 bg-white dark:bg-zinc-700 shadow-button':
                                            newCommentText.trim(),
                                        'opacity-50 cursor-not-allowed': !newCommentText.trim(),
                                    },
                                )}
                                data-testid="add-comment-card-button"
                            >
                                <CornerDownLeft
                                    size={16}
                                    className={classNames('text-saga-gray-dark ', {
                                        'dark:stroke-white stroke-gray-700': newCommentText.trim(),
                                    })}
                                />
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </PopOver>
    );
}
